import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import './Chat.scss'

const LightningIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 2L4.09 12.37A1 1 0 005 14h6l-1 8 8.91-10.37A1 1 0 0019 10h-6l1-8z" />
  </svg>
)

const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
)


const AttachIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
  </svg>
)


const Chat = () => {
  const { jobId } = useParams()

  const clientId = useMemo(() => {
    let id = localStorage.getItem('gappeo_webchat_client_id')
    if (!id) {
      id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
      localStorage.setItem('gappeo_webchat_client_id', id)
    }
    return id
  }, [])

  const [messages, setMessages] = useState([])
  const [inputText, setInputText] = useState('')
  const [refToken, setRefToken] = useState('')
  const [wsSessionToken, setWsSessionToken] = useState('')
  const [externalUserId, setExternalUserId] = useState('')
  const [isBotTyping, setIsBotTyping] = useState(false)
  const [pendingAttachment, setPendingAttachment] = useState(null)

  const wsRef = useRef(null)
  const chatMessagesRef = useRef(null)
  const fileInputRef = useRef(null)

  const wsUrl = `${process.env.REACT_APP_WS_URL}?type=WEBCHAT&webchat_client_id=${encodeURIComponent(clientId)}`

  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight
    }
  }, [messages, isBotTyping])

  useEffect(() => {
    if (!jobId) return

    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data)

        if (data.type === 'your_id') {
          const extId = `webchat-${clientId}`
          setExternalUserId(extId)
          ws.send(JSON.stringify({
            action: 'webchat_init',
            job_id: jobId,
            external_user_id: extId,
            metadata: { source: 'apply_page' },
          }))
          setIsBotTyping(true)
        }

        if (data.type === 'webchat_response') {
          if (data.external_user_id) setExternalUserId(data.external_user_id)
          if (data.ref_token) setRefToken(data.ref_token)
          if (data.ws_session_token) setWsSessionToken(data.ws_session_token)

          const historyMessages = (data.chat_history?.length > 0)
            ? data.chat_history.map((item, i) => {
                if (item.direction === 'outbound') {
                  return {
                    role: 'bot',
                    response: {
                      text: item.text,
                      template: item.buttons?.length ? { buttons: item.buttons } : null,
                      quick_replies: [],
                    },
                    buttonsClicked: true,
                    ts: i,
                  }
                }
                return {
                  role: 'user',
                  text: item.text,
                  fileAttachment: item.attachments?.[0]
                    ? { name: item.attachments[0].file_name, mimeType: item.attachments[0].mime_type, size: null }
                    : null,
                  ts: i,
                }
              })
            : []

          setMessages(prev => [
            ...prev,
            ...historyMessages,
            { role: 'bot', response: data.response, ts: Date.now() },
          ])
          setIsBotTyping(false)
        }

        if (data.type === 'webchat_error') {
          setIsBotTyping(false)
        }
      } catch (e) {
        // ignore non-json frames
      }
    }

    ws.onerror = () => setIsBotTyping(false)

    return () => ws.close()
  }, [jobId, wsUrl, clientId])

  const sendMessage = useCallback((textOverride, displayTextOverride) => {
    const text = (textOverride !== undefined ? textOverride : inputText).trim()
    const displayText = (
      displayTextOverride !== undefined ? displayTextOverride
        : textOverride !== undefined ? textOverride
          : inputText
    ).trim()
    const hasAttachment = !!pendingAttachment

    if (!text && !hasAttachment) return
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return

    const extId = externalUserId || `webchat-${clientId}`

    const clientMessageId = (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : `cm-${Date.now()}-${Math.random().toString(16).slice(2)}`

    const payload = {
      action: 'webchat_message',
      client_message_id: clientMessageId,
      external_user_id: extId,
      text: text || 'Uploaded a file',
      metadata: { source: 'apply_page' },
    }

    if (wsSessionToken) payload.ws_session_token = wsSessionToken

    if (pendingAttachment) {
      payload.attachments = [{
        mime_type: pendingAttachment.mimeType,
        content_base64: pendingAttachment.base64,
        file_name: pendingAttachment.name,
        name: pendingAttachment.name,
      }]
    }

    if (refToken) {
      payload.ref_token = refToken
    } else if (jobId) {
      payload.job_id = jobId
      payload.metadata = { ...payload.metadata, job_id: jobId }
    }

    wsRef.current.send(JSON.stringify(payload))

    setMessages(prev => [...prev, {
      role: 'user',
      text: displayText || '',
      fileAttachment: pendingAttachment ? { name: pendingAttachment.name, size: pendingAttachment.size, mimeType: pendingAttachment.mimeType } : null,
      ts: Date.now(),
    }])
    setIsBotTyping(true)
    setInputText('')
    setPendingAttachment(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [inputText, pendingAttachment, externalUserId, clientId, refToken, wsSessionToken, jobId])

  const handleButtonClick = useCallback((msgIdx, btnPayload, label) => {
    setMessages(prev => prev.map((m, i) => i === msgIdx ? { ...m, buttonsClicked: true } : m))
    sendMessage(btnPayload, label)
  }, [sendMessage])

  const processFile = async (file) => {
    if (!file) return
    const allowedMime = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ]
    const isAllowed = allowedMime.includes(file.type) || /\.(pdf|docx)$/i.test(file.name)
    if (!isAllowed) return

    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result || ''))
        reader.onerror = () => reject(new Error('read failed'))
        reader.readAsDataURL(file)
      })
      const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl
      setPendingAttachment({
        name: file.name,
        size: file.size,
        mimeType: file.type || 'application/octet-stream',
        base64,
      })
    } catch (e) {
      // ignore file read errors
    }
  }

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    if (file) await processFile(file)
    e.target.value = ''
  }

  const removeAttachment = () => {
    setPendingAttachment(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  return (
    <div className="chart-container">
      {/* Left: Chat Area */}
      <div className="chat-area">
        <div className="chat-header">
          <div className="chat-header__brand">
            <div className="brand-icon">
              {/* <LightningIcon /> */}
              <img src='/images/gappeoai-sm-logo.svg' alt='' />
            </div>
            <div className="brand-info">
              <h2 className="brand-name">Gappeo</h2>
              <div className="brand-status">
                <span className="status-dot" />
                <span className="status-text">AI Hiring Assistant</span>
              </div>
            </div>
          </div>
        </div>

        <div className="chat-messages" ref={chatMessagesRef}>
          {messages.map((msg, idx) => {
            if (msg.role === 'bot') {
              const response = msg.response || {}
              const buttons = [
                ...(response?.template?.buttons || []),
                ...(response?.quick_replies || []),
              ]
              return (
                <div key={idx} className="message">
                  <div className="message__avatar">
                    {/* <LightningIcon /> */}
                    <img src='/images/gappeoai-sm-logo.svg' alt='' />
                  </div>
                  <div className="message__bubble">
                    {response?.template?.text?.trim() || response?.text || ''}
                    {buttons.length > 0 && (
                      <div className="message-buttons">
                        {buttons.map((b, bIdx) => {
                          const label = b.title || b.label || b.payload || `Option ${bIdx + 1}`
                          if (b.type === 'web_url' && b.url) {
                            return (
                              <a
                                key={bIdx}
                                className="message-btn"
                                href={b.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {label}
                              </a>
                            )
                          }
                          const btnPayload = b.payload || label
                          return (
                            <button
                              key={bIdx}
                              className="message-btn"
                              disabled={!!msg.buttonsClicked}
                              onClick={() => handleButtonClick(idx, btnPayload, label)}
                            >
                              {label}
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )
            }

            return (
              <div key={idx} className="message message--user">
                <div className="message__bubble message__bubble--user">
                  {msg.text || null}
                  {msg.fileAttachment && (
                    <div className="file-attachment">
                      <div className="file-attachment__icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg" width="20" height="20">
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                      </div>
                      <div className="file-attachment__info">
                        <span className="file-attachment__name">{msg.fileAttachment.name}</span>
                        <span className="file-attachment__type">{msg.fileAttachment.mimeType === 'application/pdf' ? 'PDF' : 'DOCX'}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })}

          {isBotTyping && (
            <div className="message">
              <div className="message__avatar">
                {/* <LightningIcon /> */}
                <img src='/images/gappeoai-sm-logo.svg' alt='' />
              </div>
              <div className="message__bubble message__bubble--typing">
                <span className="typing-dot" />
                <span className="typing-dot" />
                <span className="typing-dot" />
              </div>
            </div>
          )}
        </div>

        {pendingAttachment && (
          <div className="pending-attachment">
            <span className="pending-attachment__name">{pendingAttachment.name}</span>
            <button className="pending-attachment__remove" type="button" onClick={removeAttachment}>
              ×
            </button>
          </div>
        )}

        <div className="chat-input-area">
          <button className="attach-btn" type="button" onClick={() => fileInputRef.current?.click()}>
            <AttachIcon />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <input
            type="text"
            className="chat-input"
            placeholder="Type your answer..."
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') sendMessage() }}
          />
          <button className="send-btn" onClick={() => sendMessage()}>
            <SendIcon />
          </button>
        </div>
      </div>

    </div>
  )
}

export default Chat
